import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["date", "scheduleWrapper", "schedule", "timeslotWrapper", "timeslot", "partySizeWrapper", "partySize", "ticketWrapper", "ticketError", "mobile", "tipField", "invoicePreview"]
  static values = { previewUrl: String }

  connect() {
    const checked = this.element.querySelector('input[name="payment_status"]:checked')
    if (checked) this.paymentStatusChanged({ target: checked })
  }

  disconnect() {
    clearTimeout(this.previewTimer)
    this.previewAbort?.abort()
  }

  paymentStatusChanged(event) {
    const showInvoice = event.target.value === "send_invoice"

    if (this.hasTipFieldTarget) {
      this.tipFieldTarget.classList.toggle("d-none", !showInvoice)
      if (!showInvoice) {
        const input = this.tipFieldTarget.querySelector("input")
        if (input) input.value = ""
      }
    }

    // Paid/comped/invoiced change the fees and tip, so the summary re-renders.
    this.fetchPreview()
  }

  // Debounced so typing a promo code or tip doesn't fire a request per keystroke.
  refreshPreview() {
    if (!this.hasInvoicePreviewTarget) return
    clearTimeout(this.previewTimer)
    this.previewTimer = setTimeout(() => this.fetchPreview(), 300)
  }

  // Posts the whole form so the server builds the order exactly as create would.
  // Aborting the previous request keeps a slow, stale response from overwriting
  // a newer one.
  fetchPreview() {
    if (!this.hasInvoicePreviewTarget) return

    this.previewAbort?.abort()
    this.previewAbort = new AbortController()

    fetch(this.previewUrlValue, {
      method: "POST",
      body: new FormData(this.element),
      headers: {
        "Accept": "text/html",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
      },
      signal: this.previewAbort.signal
    })
      .then(response => response.text())
      .then(html => {
        this.invoicePreviewTarget.innerHTML = html
      })
      .catch(error => {
        if (error.name !== "AbortError") console.error("Invoice preview failed:", error)
      })
  }

  dateChanged(event) {
    const date = event.target.value
    if (!date) return

    this.clearSchedule()
    this.clearTimeslot()
    this.clearPartySize()
    this.clearTickets()

    fetch(`/companies/schedule_info/schedules_for_date?date=${date}`, {
      headers: { "Accept": "text/html" }
    })
      .then(response => response.text())
      .then(html => {
        this.scheduleWrapperTarget.innerHTML = html
      })

    // Promo eligibility depends on the date, and the tickets were just cleared.
    this.refreshPreview()
  }

  scheduleChanged(event) {
    const scheduleId = event.target.value
    const date = this.dateTarget.value
    if (!scheduleId || !date) return

    this.clearTimeslot()
    this.clearPartySize()
    this.updateMobileRequired(event.target)

    fetch(`/companies/schedule_info/timeslots_for_schedule?date=${date}&schedule_id=${scheduleId}`, {
      headers: { "Accept": "text/html" }
    })
      .then(response => response.text())
      .then(html => {
        this.timeslotWrapperTarget.innerHTML = html
      })

    if (this.hasTicketWrapperTarget) {
      this.fetchTickets(scheduleId)
    }
  }

  fetchTickets(scheduleId) {
    fetch(`/companies/schedule_info/tickets_for_schedule?schedule_id=${scheduleId}`, {
      headers: { "Accept": "text/html" }
    })
      .then(response => response.text())
      .then(html => {
        if (this.hasTicketWrapperTarget) {
          this.ticketWrapperTarget.innerHTML = html
        }
        // Fresh ticket list means zero quantities: show the empty state.
        this.refreshPreview()
      })
  }

  updateMobileRequired(select) {
    const selectedOption = select.options[select.selectedIndex]
    const requiresPhone = selectedOption?.dataset?.requiresPhone === "true"
    this.mobileTarget.required = requiresPhone
  }

  timeslotChanged(event) {
    const timeslot = event.target.value
    const scheduleId = this.scheduleTarget.value
    const date = this.dateTarget.value
    if (!timeslot || !scheduleId || !date) return

    this.clearPartySize()

    fetch(`/companies/schedule_info/party_sizes_for_timeslot?date=${date}&schedule_id=${scheduleId}&timeslot=${timeslot}`, {
      headers: { "Accept": "text/html" }
    })
      .then(response => response.text())
      .then(html => {
        this.partySizeWrapperTarget.innerHTML = html
      })
  }

  // With a single ticket type every guest takes that ticket, so save the retyping.
  partySizeChanged(event) {
    if (!this.hasTicketWrapperTarget) return

    const ticketInputs = this.ticketWrapperTarget.querySelectorAll('input[name^="tickets["]')
    if (ticketInputs.length !== 1) return

    ticketInputs[0].value = event.target.value
    this.refreshPreview()
  }

  clearSchedule() {
    this.scheduleWrapperTarget.innerHTML = `
      <select name="reservation[schedule_id]" class="form-select" required disabled>
        <option value="">Select tour type...</option>
      </select>
    `
  }

  clearTimeslot() {
    this.timeslotWrapperTarget.innerHTML = `
      <select name="reservation[timeslot]" class="form-select" required disabled>
        <option value="">Select time...</option>
      </select>
    `
  }

  clearPartySize() {
    this.partySizeWrapperTarget.innerHTML = `
      <input type="number" name="reservation[actual_party_size]" class="form-control" required disabled
             min="1" placeholder="Number of guests"
             data-direct-reservation-target="partySize" />
    `
  }

  validateTickets(event) {
    if (!this.hasTicketWrapperTarget) return

    const ticketInputs = this.ticketWrapperTarget.querySelectorAll('input[name^="tickets["]')
    if (ticketInputs.length === 0) return

    const totalTickets = Array.from(ticketInputs).reduce((sum, input) => sum + parseInt(input.value || 0, 10), 0)
    const partySize = parseInt(this.partySizeTarget.value || 0, 10)

    if (totalTickets !== partySize) {
      event.preventDefault()
      this.ticketErrorTarget.textContent = `Total tickets (${totalTickets}) must match the party size (${partySize}).`
      this.ticketErrorTarget.classList.remove("d-none")
    } else {
      this.ticketErrorTarget.classList.add("d-none")
    }
  }

  clearTickets() {
    if (this.hasTicketWrapperTarget) {
      this.ticketWrapperTarget.innerHTML = `
        <div class="text-muted small">Select a tour type to see available tickets.</div>
      `
    }
  }
}
